import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// The hex values this page's charts have always drawn with, stated literally here rather than
// pulled from the global Chart.defaults palette the other charts use.
const GIFTS_SENT_COLOR = "#00A338";
const REDEMPTIONS_RGB = [93, 103, 204];
const REENGAGEMENT_RGB = [41, 121, 78];

export default class extends Controller {
  static get targets() {
    return [
      "giftsSentCanvas", "giftsSentTotal", "giftsSentMonth",
      "redemptionsCanvas", "redemptionsTotal", "reengagementTotal", "redemptionsMonth"
    ];
  }

  static get values() {
    return {
      updateUrl: String,
      giftsSent: Array,
      redemptions: Array,
      reengagement: Array,
      hideReengagement: Boolean
    };
  }

  connect() {
    this.renderGiftsSentChart(this.giftsSentValue);
    this.renderRedemptionsChart(this.redemptionsValue, this.reengagementValue);
  }

  disconnect() {
    if (this.giftsSentChart) { this.giftsSentChart.destroy(); }
    if (this.redemptionsChart) { this.redemptionsChart.destroy(); }
  }

  reloadGiftsSent(event) {
    const month = this.monthParam(event.target.value);

    fetch(`${this.updateUrlValue}?graph=gifts_sent&month=${month}`, RequestHelper.defaultRequestOptions()).
      then(response => {
        if (!response.ok) { throw new Error(`Chart reload failed (${response.status})`); }
        return response.json();
      }).
      then(data => this.renderGiftsSentChart(data.gifts_sent)).
      catch(error => console.warn(error));
  }

  reloadRedemptions(event) {
    const month = this.monthParam(event.target.value);

    fetch(`${this.updateUrlValue}?graph=redemptions&month=${month}`, RequestHelper.defaultRequestOptions()).
      then(response => {
        if (!response.ok) { throw new Error(`Chart reload failed (${response.status})`); }
        return response.json();
      }).
      then(data => this.renderRedemptionsChart(data.redemptions, data.reengagement)).
      catch(error => console.warn(error));
  }

  // The select's value is "YYYY MM DD" (a Rails options_for_select key); the endpoint wants
  // "MM/DD/YYYY".
  monthParam(value) {
    const [year, month, day] = value.split(" ");
    return `${month}/${day}/${year}`;
  }

  renderGiftsSentChart(giftsSentData) {
    if (!this.hasGiftsSentCanvasTarget) { return; }

    const labels = giftsSentData.map((_count, index) => `Week ${index + 1}`);

    if (this.giftsSentChart) { this.giftsSentChart.destroy(); }
    this.giftsSentChart = new Chart(this.giftsSentCanvasTarget, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [{
          label: "Total Gifts Sent",
          data: giftsSentData,
          backgroundColor: GIFTS_SENT_COLOR,
          borderWidth: 0,
          barThickness: 50
        }]
      },
      options: {
        aspectRatio: 3,
        tooltips: { callbacks: { title: (items) => labels[items[0].index] } },
        scales: {
          yAxes: [{
            gridLines: { color: "#F6EFE1", drawBorder: false, borderDash: [8, 8] },
            ticks: {
              display: false, beginAtZero: true, suggestedMax: 4, suggestedMin: 4,
              maxTicksLimit: 4, minStepSize: 1
            }
          }],
          xAxes: [{
            gridLines: { color: "#F6EFE1", borderDash: [8, 8] },
            ticks: { fontFamily: "'Söhne', sans-serif", fontSize: 14 }
          }]
        }
      }
    });

    // total is a sum of the gifts-sent-per-week counts from giftsSentValue (server JSON, or the
    // update_graph_dashboard_path fetch response) -- always a number, never HTML.
    const total = giftsSentData.reduce((sum, count) => sum + (count || 0), 0);
    if (this.hasGiftsSentTotalTarget) { this.giftsSentTotalTarget.textContent = total; }
  }

  renderRedemptionsChart(redemptionsData, reengagementData) {
    if (!this.hasRedemptionsCanvasTarget) { return; }

    const labels = redemptionsData.map((_count, index) => `Week ${index + 1}`);
    const ctx = this.redemptionsCanvasTarget.getContext("2d");

    // TODO: We need to figure out how to make the size of the gradient dynamic
    const redemptionsGradient = ctx.createLinearGradient(0, 0, 0, 744);
    redemptionsGradient.addColorStop(0, `rgba(${REDEMPTIONS_RGB.join(",")}, 0.16)`);
    redemptionsGradient.addColorStop(1, `rgba(${REDEMPTIONS_RGB.join(",")}, 0)`);

    const reengagementGradient = ctx.createLinearGradient(0, 0, 0, 744);
    reengagementGradient.addColorStop(0, `rgba(${REENGAGEMENT_RGB.join(",")},0.16)`);
    reengagementGradient.addColorStop(1, `rgba(${REENGAGEMENT_RGB.join(",")},0)`);

    const datasets = [
      {
        label: "Redemptions",
        data: redemptionsData,
        borderColor: `rgb(${REDEMPTIONS_RGB.join(",")})`,
        pointBackgroundColor: `rgb(${REDEMPTIONS_RGB.join(",")})`,
        pointBorderColor: "#fff",
        backgroundColor: redemptionsGradient,
        pointBorderWidth: 3,
        pointRadius: 10,
        borderWidth: 5
      }
    ];
    if (!this.hideReengagementValue) {
      datasets.push({
        label: "Re-engagement",
        data: reengagementData,
        borderColor: `rgb(${REENGAGEMENT_RGB.join(",")})`,
        pointBackgroundColor: `rgb(${REENGAGEMENT_RGB.join(",")})`,
        pointBorderColor: "#fff",
        backgroundColor: reengagementGradient,
        pointBorderWidth: 3,
        pointRadius: 10,
        borderWidth: 5
      });
    }

    if (this.redemptionsChart) { this.redemptionsChart.destroy(); }
    this.redemptionsChart = new Chart(this.redemptionsCanvasTarget, {
      type: "line",
      data: { labels: labels, datasets: datasets },
      options: {
        aspectRatio: 3,
        datasetFill: false,
        tooltips: { callbacks: { title: (items) => labels[items[0].index] } },
        scales: {
          yAxes: [{
            gridLines: { drawBorder: false, color: "#F6EFE1", borderDash: [8, 8] },
            ticks: {
              display: false, beginAtZero: true, suggestedMax: 4, suggestedMin: 4,
              maxTicksLimit: 4, minStepSize: 1
            }
          }],
          xAxes: [{
            gridLines: { color: "#F6EFE1", borderDash: [8, 8] },
            ticks: { fontFamily: "'Söhne', sans-serif", fontSize: 14 }
          }]
        }
      }
    });

    // Totals are sums of numeric per-week counts, never HTML.
    const reengagementTotal = reengagementData.reduce((sum, count) => sum + (count || 0), 0);
    const redemptionsTotal = redemptionsData.reduce((sum, count) => sum + (count || 0), 0);
    if (this.hasRedemptionsTotalTarget) { this.redemptionsTotalTarget.textContent = redemptionsTotal; }
    if (this.hasReengagementTotalTarget) { this.reengagementTotalTarget.textContent = reengagementTotal; }
  }
}
